import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { Assessment as ReportIcon, QuestionAnswer as QuestionIcon } from '@mui/icons-material';
import { CompleteAuditConfirmationData, AuditProgressDTO } from '../types';

interface CompleteAuditDialogProps {
  open: boolean;
  onClose: () => void;
  auditId: string;
  progress: AuditProgressDTO;
  confirmationData?: CompleteAuditConfirmationData;
  onPublish: () => void;
  isPublishing?: boolean;
  canPublish?: boolean;
}

export const CompleteAuditDialog: React.FC<CompleteAuditDialogProps> = ({
  open,
  onClose,
  auditId: _auditId,
  progress: _progress,
  confirmationData,
  onPublish,
  isPublishing = false,
  canPublish = false,
}) => {
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [notes, setNotes] = useState('');

  const handleClose = () => {
    setConfirmationChecked(false);
    setNotes('');
    onClose();
  };

  const handlePublish = () => {
    onPublish();
  };

  const renderPublishMode = () => {
    const hasUncompletedQuestions = (confirmationData?.uncompletedQuestions?.length ?? 0) > 0;

    return (
      <>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportIcon color="success" />
            <Typography variant="h6">Generate Report</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Warnings */}
          {hasUncompletedQuestions && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                The following questions have not been reviewed yet:
              </Typography>
              <List dense>
                {confirmationData?.uncompletedQuestions.map((questionId) => (
                  <ListItem key={questionId} sx={{ py: 0 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      <QuestionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`Question ${questionId}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {/* Confirmation */}
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmationChecked}
                onChange={(e) => setConfirmationChecked(e.target.checked)}
              />
            }
            label="I confirm that I have reviewed all responses and am ready to generate the report"
            sx={{ mb: 2 }}
          />

          {/* Report Notes */}
          <TextField
            label="Report Notes (Optional)"
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this audit review..."
            sx={{ mb: 2 }}
          />

          <Alert severity="info">
            <Typography variant="body2">
              Generating the report will complete the audit review, make the results available in
              reports, and lock the audit from further changes. This action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} disabled={isPublishing}>
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            variant="contained"
            color="success"
            disabled={!confirmationChecked || isPublishing || !canPublish}
            startIcon={isPublishing ? <CircularProgress size={16} /> : <ReportIcon />}
          >
            {isPublishing ? 'Generating...' : 'Generate Report'}
          </Button>
        </DialogActions>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isPublishing}
    >
      {renderPublishMode()}
    </Dialog>
  );
};
